import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const BACKEND_ROOT = BACKEND_URL;

/**
 * Build an absolute URL for displaying product images.
 * Handles:
 *  - new uploads (path like "fabtexs/sellers/.../thumb.jpg")
 *  - backend-relative URLs like "/api/files/..."
 *  - legacy absolute URLs (Unsplash etc.) — returned as-is
 */
export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith('/api/')) return `${BACKEND_URL}${pathOrUrl}`;
  return `${BACKEND_URL}/api/files/${pathOrUrl}`;
}

/**
 * Returns the best image URL for a product (handles new + legacy schema).
 * @param {object} product
 * @param {'thumb'|'medium'|'full'|'original'} size
 */
export function productImage(product, size = 'medium') {
  if (!product) return '';
  if (product.images && product.images.length > 0) {
    const primary = product.images.find((i) => i.is_primary) || product.images[0];
    const path = primary.paths?.[size] || primary.paths?.medium || primary.paths?.thumb;
    if (path) return fileUrl(path);
  }
  // legacy
  return fileUrl(product.processed_image_url || product.original_image_url);
}

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  // Per-tab admin impersonation token (scopes seller pages to the selected
  // seller) takes precedence, but ONLY in the tab that set it — the admin's
  // own tab keeps using its localStorage session untouched.
  const token = sessionStorage.getItem('fab_imp_token') || localStorage.getItem('fab_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      // In an admin impersonation tab, never clear the admin's own session.
      if (sessionStorage.getItem('fab_imp_token')) return Promise.reject(err);
      localStorage.removeItem('fab_token');
      if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/signup') && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;
